// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAnalysisRunHistory } from '../../../services/AnalysisApiClient';
import { isServerBackedAnalysisId } from '../../../utils/isServerBackedAnalysisId';
import { useCompanyContext } from '../context/CompanyContext';
import { RunHistoryPanel, filterRunHistoryTableRows, mapClientRunHistoryToTableRows } from '../runHistory';
import { ClientPageSummaryBar } from '../components/ClientPageSummaryBar';
import { ClientEmptyStateCard } from '../../shared/ClientEmptyStateCard';
import { FileClock } from 'lucide-react';

export default function RunHistoryPage() {
  const navigate = useNavigate();
  const { currentAnalysisId, currentAnalysis, isCurrentAnalysisLocked, setCurrentAnalysisId } = useCompanyContext();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const analysisId = String(currentAnalysisId ?? '').trim();
  const hasValidAnalysis = isServerBackedAnalysisId(analysisId);

  useEffect(() => {
    if (!isServerBackedAnalysisId(analysisId)) {
      setRows([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    getAnalysisRunHistory(analysisId, 50)
      .then((r) => setRows(Array.isArray(r) ? r : []))
      .catch((e) => {
        console.error(e);
        setError(e instanceof Error ? e.message : 'Failed to load analysis run history');
      })
      .finally(() => setLoading(false));
  }, [analysisId]);

  const tableRows = useMemo(() => mapClientRunHistoryToTableRows(analysisId, rows), [analysisId, rows]);

  const filteredRows = useMemo(() => filterRunHistoryTableRows(tableRows, searchQuery), [tableRows, searchQuery]);

  const analysisNameSubline = useMemo(() => {
    if (!hasValidAnalysis || !currentAnalysis || String(currentAnalysis.id ?? '') !== analysisId) return null;
    if (currentAnalysis.createdBy || currentAnalysis.createdOn) {
      return { createdBy: currentAnalysis.createdBy, createdOn: currentAnalysis.createdOn };
    }
    return null;
  }, [hasValidAnalysis, currentAnalysis, analysisId]);

  const runCount = tableRows.length;
  const empty = !loading && hasValidAnalysis && runCount === 0;
  const noAnalysisSelected = !loading && !hasValidAnalysis;

  return (
    <div className="space-y-3">
      <ClientPageSummaryBar
        intro={
          hasValidAnalysis ? (
            <p>
              Saved <strong className="font-semibold text-gray-700">Run 280G Results</strong> snapshots for your open
              analysis. <strong className="font-semibold text-gray-700">View</strong> opens one read-only.
            </p>
          ) : (
            <p>
              Run history is listed per analysis. Open an analysis from{' '}
              <strong className="font-semibold text-gray-700">280G Analyses</strong>, then return here to browse stored
              runs for that workspace.
            </p>
          )
        }
      />
      <RunHistoryPanel
        error={error}
        loading={loading}
        totalRowCount={runCount}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        searchInputId="client-run-history-search"
        displayRows={filteredRows}
        linkAnalysisNames
        onClientAnalysisNameClick={(id) => setCurrentAnalysisId(id)}
        analysisNameSubline={analysisNameSubline}
        showAnalysisNameLock={hasValidAnalysis}
        analysisNameLocked={isCurrentAnalysisLocked}
        onViewSnapshot={(r) =>
          navigate(`/client/run-history/${encodeURIComponent(r.analysisId)}/${encodeURIComponent(r.runId)}`)
        }
        emptyState={
          noAnalysisSelected ? (
            <ClientEmptyStateCard icon={FileClock} title="No analysis selected.">
              <p>
                Go to <strong className="text-gray-600">280G Analyses</strong> and open an analysis. Run history below
                updates for whichever analysis is active in your workspace.
              </p>
            </ClientEmptyStateCard>
          ) : empty ? (
            <ClientEmptyStateCard icon={FileClock} title="No run history recorded yet.">
              <p>
                Click <strong className="text-gray-600">Run 280G Results</strong> on the 280G results page to record a
                run and store inputs plus results. Open <strong className="text-gray-600">View</strong> on a row below
                to browse a stored snapshot.
              </p>
            </ClientEmptyStateCard>
          ) : null
        }
      />
    </div>
  );
}
